import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgxFileDropComponent, NgxFileDropEntry } from 'ngx-file-drop';
import { Observable, Subject, combineLatest, filter, map, switchMap, take, tap } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { PermissionService } from 'src/app/core/services/permission.service';
import { AppState } from 'src/app/core/store/state';
import { isPresent } from 'src/app/core/utils/isPresent';
import { prepareCreateWorkspacePayload } from 'src/app/core/utils/workspaces.utils';
import { GetDocumentTypeDto } from 'src/app/modules/doc-types/models/doc-types.model';
import { DocumentTypesApiService } from 'src/app/modules/doc-types/services/document-types-api.service';
import { UserDto } from 'src/app/modules/login/services/user-api.service';
import { AlertType } from 'src/app/shared/components/app-alert/app-alert.component';
import {
  AppBulkEditSingleValuePropertyComponent,
  BulkEditValueModel,
} from 'src/app/shared/components/bulk-edit/app-bulk-edit-single-value-property/app-bulk-edit-single-value-property.component';
import {
  ControlType,
  InlineEditTableColumn,
} from 'src/app/shared/components/data-table/client-side-inline-edit-data-table/client-side-inline-edit-data-table.component';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { PositionDto, PositionsApiService } from 'src/app/shared/services/positions-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { selectContentLanguages } from 'src/app/shared/store/selectors';
import { isPlural } from 'src/app/shared/utils';
import {
  WorkspaceDetailsDto,
  WorkspaceDocumentDto,
  WorkspaceUpdateModel,
} from '../../../models/workspaces.model';
import { WorkspacesActions } from '../../../store/actions';
import {
  selectAddingWorkspaceDocuments,
  selectCloseAddingWorkspaceDocumentsModal,
  selectWorkspaceDetails,
} from '../../../store/selectors';
import { WorkspaceDocumentUploadStep } from '../add-workspace-modal/add-workspace-modal.models';

@UntilDestroy()
@Component({
  selector: 'app-add-workspace-documents-modal',
  styleUrl: './add-workspace-documents-modal.component.scss',
  templateUrl: './add-workspace-documents-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddWorkspaceDocumentsModalComponent implements OnInit, AfterViewInit {
  public onClose: Subject<boolean> = new Subject<boolean>();
  public title: string = this.translocoService.translate(`workspaces.addDocuments`);
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public alertTypes = AlertType;
  public constants = constants;

  public user$: Observable<UserDto | null> | undefined;
  public currentUser: UserDto | null | undefined;
  public isAddingWorkspaceDocuments$ = this.store$.select(selectAddingWorkspaceDocuments);

  public maxFileCountError: boolean = false;
  public files: NgxFileDropEntry[] = [];
  public wrongFileType: boolean = false;
  public wrongFileTypeErrorMessage: string = '';
  public contentLanguages: LanguageDto[] = [];
  public positionItems: PositionDto[] = [];
  public docTypeItems: GetDocumentTypeDto[] = [];
  public workspaceDocumentsForEdit: WorkspaceDocumentDto[] = [];
  public selectedWorkspaceDocumentIds: string[] = [];
  public isWorkspaceDocumentsTableValid: boolean = false;
  public workspaceDocumentsColumns: InlineEditTableColumn[] = [];
  public isBulkEditModalOpen: boolean = false;
  public workspace: WorkspaceDetailsDto = {} as WorkspaceDetailsDto;
  public alreadyExistWorkspaceDocuments = [] as WorkspaceDocumentDto[];
  public workspaceDocumentUploadStep: WorkspaceDocumentUploadStep =
    WorkspaceDocumentUploadStep.SelectDocuments;
  public canWrite = false;
  private canWriteFileUpload = false;
  private canWriteDocumentTypes = false;
  @ViewChild('fileDropRef') public fileDropRef?: NgxFileDropComponent;

  constructor(
    private store$: Store<AppState>,
    private readonly bsModalService: BsModalService,
    private bsModalRef: BsModalRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
    private readonly positionsApiService: PositionsApiService,
    private readonly documentTypesApiService: DocumentTypesApiService,
    private readonly permissionService: PermissionService,
  ) {}

  ngAfterViewInit(): void {
    this.store$
      .select(selectCloseAddingWorkspaceDocumentsModal)
      .pipe(untilDestroyed(this))
      .subscribe((close) => {
        if (close) {
          this.store$.dispatch(WorkspacesActions.clearAddWorkspaceDocumentsCancelled());
          this.bsModalRef.hide();
        }
      });
  }

  public ngOnInit(): void {
    this.initWorkspaceDetails();
    this.setWorkspaceDocumentsColumns();
    this.initContentLanguages();
    this.initPositionItems();
    this.initDocTypeItems();
    combineLatest([
      this.permissionService.canWrite$(PermissionResource.Workspaces),
      this.permissionService.canWrite$(PermissionResource.FileUpload),
      this.permissionService.canWrite$(PermissionResource.DocumentTypes),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([canWrite, canWriteFileUpload, canWriteDocumentTypes]) => {
        this.canWrite = canWrite;
        this.canWriteFileUpload = canWriteFileUpload;
        this.canWriteDocumentTypes = canWriteDocumentTypes;
        this.setWorkspaceDocumentsColumns();
        if (!this.canUploadFiles) {
          this.resetWorkspaceDocumentsSelections();
        }
        this.cdr.markForCheck();
      });
  }

  private initWorkspaceDetails(): void {
    this.store$
      .select(selectWorkspaceDetails)
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe((workspaceDetails) => {
        this.workspace = workspaceDetails;
        this.alreadyExistWorkspaceDocuments = workspaceDetails.documents || [];
        this.cdr.markForCheck();
      });
  }

  private initContentLanguages(): void {
    this.store$
      .select(selectContentLanguages)
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe((contentLanguages) => {
        this.contentLanguages = contentLanguages;
        this.setWorkspaceDocumentsColumns();
        this.cdr.markForCheck();
      });
  }

  private initPositionItems(): void {
    this.positionsApiService
      .getPositions$()
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe((positionItems) => {
        this.positionItems = positionItems;
        this.setWorkspaceDocumentsColumns();
        this.cdr.markForCheck();
      });
  }

  private initDocTypeItems(): void {
    this.documentTypesApiService
      .getDocumentTypes$()
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe((docTypeItems) => {
        this.docTypeItems = docTypeItems;
        this.setWorkspaceDocumentsColumns();
        this.cdr.markForCheck();
      });
  }

  private createDocumentType(name: string) {
    return this.documentTypesApiService.createDocumentType$(name).pipe(
      switchMap((newDocumentType) => {
        return this.documentTypesApiService.getDocumentTypes$().pipe(
          tap((docTypeItems) => {
            this.docTypeItems = docTypeItems;
            this.setWorkspaceDocumentsColumns();
            this.cdr.markForCheck();
          }),
          map(() => newDocumentType),
        );
      }),
    );
  }

  private setWorkspaceDocumentsColumns(): void {
    this.workspaceDocumentsColumns = [
      {
        field: 'name',
        header: this.translocoService.translate('columns.docName'),
        required: true,
        editable: false,
      },
      {
        field: 'type',
        header: this.translocoService.translate('columns.docType'),
        required: true,
        controlType: ControlType.SingleSelectAutocompleteWithCreate,
        autocompleteOptions: this.docTypeItems,
        optionLabel: 'name',
        optionValue: 'id',
        createOptionMethod: this.canCreateNewDocType ? this.createDocumentType.bind(this) : undefined,
        createErrorKey: 'docTypeNameAlreadyExist',
      },
      {
        field: 'userPositionId',
        header: this.translocoService.translate('columns.myPosition'),
        required: true,
        controlType: ControlType.SingleSelectAutocomplete,
        autocompleteOptions: this.positionItems,
        optionLabel: 'name',
        optionValue: 'id',
      },
      {
        field: 'languageId',
        header: this.translocoService.translate('columns.language'),
        required: true,
        controlType: ControlType.SingleSelectAutocomplete,
        autocompleteOptions: this.contentLanguages,
        optionLabel: 'displayName',
        optionValue: 'id',
      },
    ];
  }

  public onAddMoreFiles(): void {
    if (!this.canUploadFiles) {
      return;
    }

    this.fileDropRef?.openFileSelector();
  }

  public onRemoveFile(index: number): void {
    if (!this.canUploadFiles) {
      return;
    }

    this.files.splice(index, 1);
    this.maxFileCountError = false;
    this.cdr.markForCheck();
  }

  public async onConfirm(): Promise<void> {
    if (!this.canUploadFiles) {
      return;
    }

    const { metadata, filesById } = await prepareCreateWorkspacePayload(
      this.workspaceDocumentsForEdit,
      this.files,
    );

    const model = {
      id: this.workspace.id,
      metadata,
      filesById,
    } as WorkspaceUpdateModel;

    this.store$.dispatch(WorkspacesActions.addWorkspaceDocuments({ model }));
    this.bsModalRef.hide();
  }

  public onBack(): void {
    this.workspaceDocumentUploadStep = WorkspaceDocumentUploadStep.SelectDocuments;
    this.resetWorkspaceDocumentsSelections();
    this.cdr.markForCheck();
  }

  public onNext(): void {
    if (!this.canUploadFiles) {
      return;
    }

    this.workspaceDocumentsForEdit = this.files.map((file) => {
      return {
        id: file.relativePath,
        name: file.fileEntry.name,
        addedAt: '',
        type: undefined,
        userPositionId: undefined,
        languageId: undefined,
      };
    });

    this.workspaceDocumentUploadStep = WorkspaceDocumentUploadStep.SetDocumentProperties;
    this.isWorkspaceDocumentsTableValid = false;
    this.resetWorkspaceDocumentsSelections();
    this.cdr.markForCheck();
  }

  public onWorkspaceDocumentsDataChange(data: WorkspaceDocumentDto[]): void {
    this.workspaceDocumentsForEdit = data;

    const documentIds = new Set(data.map((item) => item.id));
    this.selectedWorkspaceDocumentIds = this.selectedWorkspaceDocumentIds.filter((id) =>
      documentIds.has(id),
    );
  }

  public onWorkspaceDocumentsValidityChange(valid: boolean): void {
    this.isWorkspaceDocumentsTableValid = valid;
    this.cdr.markForCheck();
  }

  public onWorkspaceDocumentsSelectionChange(selectedIds: Array<string | number>): void {
    this.selectedWorkspaceDocumentIds = selectedIds.map((id) => String(id));
    this.cdr.markForCheck();
  }

  public selectAllWorkspaceDocuments(): void {
    this.selectedWorkspaceDocumentIds = this.workspaceDocumentsForEdit.map((item) => item.id);
    this.cdr.markForCheck();
  }

  public resetWorkspaceDocumentsSelections(): void {
    this.selectedWorkspaceDocumentIds = [];
    this.cdr.markForCheck();
  }

  public bulkEditDocType(): void {
    if (!this.canWrite) {
      return;
    }

    const modal = this.bsModalService.show(AppBulkEditSingleValuePropertyComponent, {
      class: 'workspace-bulk-edit-offset',
      initialState: {
        titleKey: 'modalBody.bulkEditDocumentType',
        countItemsWillBeUpdatedKey: this.textCountDocsWillBeUpdated,
        fieldToBeModifiedName: constants.fields.documentType,
        mainItemIdKey: 'id',
        mainItems: this.workspaceDocumentsForEdit,
        options: this.docTypeItems,
        selectedMainItemsIds: this.selectedWorkspaceDocumentIds,
      },
    });
    this.registerBulkEditModalLifecycle(modal);

    modal.content?.continue$
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe((model) => this.updateWorkspaceDocsByBulkEditOperation(model));
  }

  public bulkEditPosition(): void {
    if (!this.canWrite) {
      return;
    }

    const modal = this.bsModalService.show(AppBulkEditSingleValuePropertyComponent, {
      class: 'workspace-bulk-edit-offset',
      initialState: {
        titleKey: 'modalBody.bulkEditDocumentPosition',
        countItemsWillBeUpdatedKey: this.textCountDocsWillBeUpdated,
        fieldToBeModifiedName: constants.fields.documentUserPosition,
        mainItemIdKey: 'id',
        mainItems: this.workspaceDocumentsForEdit,
        options: this.positionItems,
        selectedMainItemsIds: this.selectedWorkspaceDocumentIds,
      },
    });
    this.registerBulkEditModalLifecycle(modal);

    modal.content?.continue$
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe((model) => this.updateWorkspaceDocsByBulkEditOperation(model));
  }

  public bulkEditLanguage(): void {
    if (!this.canWrite) {
      return;
    }

    const modal = this.bsModalService.show(AppBulkEditSingleValuePropertyComponent, {
      class: 'workspace-bulk-edit-offset',
      initialState: {
        titleKey: 'modalBody.bulkEditDocumentLanguage',
        countItemsWillBeUpdatedKey: this.textCountDocsWillBeUpdated,
        fieldToBeModifiedName: constants.fields.documentLanguage,
        mainItemIdKey: 'id',
        mainItems: this.workspaceDocumentsForEdit,
        options: this.contentLanguages,
        optionLabelKey: 'displayName',
        selectedMainItemsIds: this.selectedWorkspaceDocumentIds,
      },
    });
    this.registerBulkEditModalLifecycle(modal);

    modal.content?.continue$
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe((model) => this.updateWorkspaceDocsByBulkEditOperation(model));
  }

  private updateWorkspaceDocsByBulkEditOperation(model: BulkEditValueModel): void {
    const selectedIds = new Set(model.selectedMainItemsIds.map((itemId) => String(itemId)));

    this.workspaceDocumentsForEdit = this.workspaceDocumentsForEdit.map((item) => {
      const itemId = String((item as any)[model.mainItemIdKey]);

      if (!selectedIds.has(itemId)) {
        return item;
      }

      return {
        ...item,
        [model.fieldToBeModifiedName]: model.checkedOptionValue,
      };
    });

    this.cdr.markForCheck();
  }

  private registerBulkEditModalLifecycle(modal: BsModalRef): void {
    this.isBulkEditModalOpen = true;
    this.cdr.markForCheck();

    modal.onHidden?.pipe(untilDestroyed(this), take(1)).subscribe(() => {
      this.isBulkEditModalOpen = false;
      this.cdr.markForCheck();
    });
  }

  public onCancel(): void {
    this.store$.dispatch(WorkspacesActions.addWorkspaceDocumentsCancel({ pristine: true }));
  }

  public dropped(files: NgxFileDropEntry[]) {
    if (!this.canUploadFiles) {
      return;
    }

    const existingPaths = new Set(this.files.map((file) => file.relativePath.toLowerCase()));
    const uniqueFiles = files.filter((file) => {
      const pathKey = file.relativePath.toLowerCase();

      if (existingPaths.has(pathKey)) {
        return false;
      }

      existingPaths.add(pathKey);

      return true;
    });

    this.maxFileCountError = this.files.length + uniqueFiles.length > 10;
    this.wrongFileType = false;

    if (this.maxFileCountError) {
      return;
    }

    uniqueFiles.forEach((droppedFile) => {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        fileEntry.file((file: File) => {
          if (constants.fileUploaderAcceptedFormatsTypes.indexOf(file.type) !== -1) {
            this.files.push(droppedFile);
          } else {
            this.wrongFileType = true;
          }

          this.cdr.markForCheck();
        });
      }
    });
  }

  public get isFileSelected(): boolean {
    return this.files?.length > 0;
  }

  public get canUploadFiles(): boolean {
    return this.canWrite && this.canWriteFileUpload;
  }

  public get canCreateNewDocType(): boolean {
    return this.canWrite && this.canWriteDocumentTypes;
  }

  get isSelectDocumentsStep(): boolean {
    return this.workspaceDocumentUploadStep === WorkspaceDocumentUploadStep.SelectDocuments;
  }

  get isSetDocumentPropertiesStep(): boolean {
    return this.workspaceDocumentUploadStep === WorkspaceDocumentUploadStep.SetDocumentProperties;
  }

  get isHideTabs(): boolean {
    return this.isSetDocumentPropertiesStep;
  }

  get isAnySelectedWorkspaceDocuments(): boolean {
    return this.selectedWorkspaceDocumentIds.length > 0;
  }

  get selectedWorkspaceDocumentsCount(): number {
    return this.selectedWorkspaceDocumentIds.length;
  }

  get workspaceDocumentsCount(): number {
    return this.workspaceDocumentsForEdit.length;
  }

  get textCountDocsWillBeUpdated(): string {
    return isPlural(this.selectedWorkspaceDocumentsCount)
      ? 'text.countDocsWillBeUpdated'
      : 'text.countDocsWillBeUpdatedSingle';
  }
}
